import type { PatientCoverage } from '@features/patient-coverages/types';
import { TextField } from '@mui/material';
import { useEventTracking } from '@services/statsig';

import { eligibilityCoverageLabel } from '../utils/eligibilityCoverageLabel';

interface Props {
  coverages: PatientCoverage[];
  selectedCoverageId: string;
  onSelect: (coverageId: string) => void;
  disabled?: boolean;
}

/** Selection is controlled by the workspace, never inferred from a snapshot. */
export function CoverageEligibilitySelector({
  coverages,
  selectedCoverageId,
  onSelect,
  disabled = false,
}: Props) {
  const { eligibilityRecord: tracking } = useEventTracking();
  const selectedExists = coverages.some(
    coverage => coverage.patient_insurance_coverage_id === selectedCoverageId,
  );
  return (
    <TextField
      select
      label="Coverage for eligibility"
      fullWidth
      sx={{
        maxWidth: 420,
        mt: 1,
        '& .MuiInputBase-root': { fontSize: 12.5 },
        '& .MuiFormHelperText-root': { mx: 0, fontSize: 11 },
      }}
      size="small"
      value={selectedExists ? selectedCoverageId : ''}
      disabled={disabled || coverages.length === 0}
      slotProps={{ select: { native: true }, inputLabel: { shrink: true } }}
      onChange={event => {
        const id = event.target.value;
        if (coverages.some(coverage => coverage.patient_insurance_coverage_id === id)) {
          tracking.coverageSelected();
          onSelect(id);
        }
      }}
      helperText={
        coverages.length === 0
          ? 'Add insurance coverage before recording benefits.'
          : disabled
            ? 'Finish or cancel the open entry before changing coverage.'
            : undefined
      }
    >
      <option value="">Choose a coverage</option>
      {coverages.map(coverage => (
        <option
          key={coverage.patient_insurance_coverage_id}
          value={coverage.patient_insurance_coverage_id}
        >
          {eligibilityCoverageLabel(coverage)}
        </option>
      ))}
    </TextField>
  );
}
